/**
 * Run at most ONE redraw per animation frame, trailing edge.
 *
 * WHY THIS EXISTS AS A SHARED THING. `<input type="range">` raises `input` on every
 * pointer move — several times per animation frame during a drag. The chart screens
 * each answered every one of those with a full synchronous redraw.
 *
 * MEASURED on the temperature screen: a redraw costs ~75 ms and does NOT scale with
 * cure length (630 ticks and 230 ticks both land around 75 ms), so the cost is
 * per-redraw work, not data volume. At several events per frame against a 16 ms
 * budget the main thread cannot keep up, the event queue grows without bound, and
 * the tab stops responding — which is exactly the "this page isn't responding" kill
 * reported from the bench while dragging the /graph time slider.
 *
 * Coalescing is also what the desktop does: `GraphViewModel.InternalUpdate` runs the
 * update on a task and, if one is already running, queues AT MOST ONE follow-up and
 * drops the rest. A fast drag there never queues N frames either.
 *
 * TRAILING EDGE, deliberately: the newest value wins. The intermediate positions
 * during a drag are precisely the ones nobody sees, so rendering them is pure waste
 * — and rendering them is what makes the browser give up.
 *
 * Extracted from the proven implementation in temperature.component so the other
 * screens get the same behaviour rather than three copies that drift. It holds no
 * chart state, so it is testable without a surface.
 */
export class FrameCoalescer<T> {
  private frame: number | null = null;
  private pending: T | null = null;
  private hasPending = false;

  constructor(
    private readonly run: (value: T) => void,
    /** Injectable for tests; defaults to the real scheduler. */
    private readonly schedule: (cb: () => void) => number = requestAnimationFrame.bind(window),
    private readonly unschedule: (h: number) => void = cancelAnimationFrame.bind(window)
  ) {}

  /** Newest value wins. Safe to call many times per frame. */
  submit(value: T): void {
    this.pending = value;
    this.hasPending = true;
    if (this.frame !== null) {
      return;
    }
    this.frame = this.schedule(() => {
      this.frame = null;
      if (!this.hasPending) {
        return;
      }
      const next = this.pending as T;
      this.pending = null;
      this.hasPending = false;
      this.run(next);
    });
  }

  /**
   * Drop a scheduled frame that has not run.
   *
   * MUST be called on teardown. Without it a queued callback redraws a surface that
   * is being deleted, and SciChart throws on a deleted context — the only sign
   * being a console error during navigation.
   */
  cancel(): void {
    if (this.frame !== null) {
      this.unschedule(this.frame);
      this.frame = null;
    }
    this.pending = null;
    this.hasPending = false;
  }

  /** True while a frame is scheduled. For assertions and diagnostics. */
  get scheduled(): boolean {
    return this.frame !== null;
  }
}
